const MAX_PUBLIC_ERROR_CHARS = 300;
// A request that dies after this long was cut off mid-flight, not unreachable.
const SLOW_NETWORK_FAILURE_MS = 15_000;

function safeMessage(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const firstLine = value.split(/\r?\n/, 1)[0]?.trim() ?? '';
  if (!firstLine) return null;

  // Never render server stacks, local paths, or HTML error pages in the admin UI.
  if (
    /file:\/\/|node:internal|<html|<!doctype|\bat\s+\S+\s+\(|(?:^|[\s'"])\/(?:Users|home|app|var|tmp|opt|srv|workspace)\/|[a-z]:\\|\b(?:ENOENT|EACCES|SQLSTATE)\b|duplicate key|database constraint/i
      .test(firstLine)
  ) {
    return null;
  }

  return firstLine.slice(0, MAX_PUBLIC_ERROR_CHARS);
}

/** Convert a failed API response into short, safe coach-facing text. */
export function getMastraErrorMessage(responseText: string, status: number): string {
  if (status >= 500) {
    return `Request failed (${status}). Try again.`;
  }

  try {
    const parsed = JSON.parse(responseText) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const body = parsed as { message?: unknown; error?: unknown };
      const structured = safeMessage(body.message) ?? safeMessage(body.error);
      if (structured) return structured;
      return `Request failed (${status}). Try again.`;
    }
  } catch {
    // Plain-text responses are handled below.
  }

  return safeMessage(responseText) ?? `Request failed (${status}). Try again.`;
}

/** Explain a request that failed before the workout service sent any response. */
export function getMastraNetworkErrorMessage(error: unknown, elapsedMs: number): string {
  if (error instanceof Error && error.name === 'AbortError') {
    return 'Request timed out. Try again.';
  }
  if (elapsedMs >= SLOW_NETWORK_FAILURE_MS) {
    return 'The workout service stopped responding before it finished. Try again.';
  }
  return 'Could not reach the workout service. Check your connection and try again.';
}
